import { Alert, Card, Select, Spin, Table, Tag, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { fetchCrowsNestUnadoptedVenues } from 'src/utils/crowsNestApi'
import { CROWS_NEST_TOOLTIPS } from 'src/utils/crowsNestTooltips'
import {
  CROWS_NEST_UNADOPTED_FEATURE_FILTERS,
  CROWS_NEST_UNADOPTED_PAGE_SIZE,
  CROWS_NEST_UNADOPTED_PLAN_FILTERS,
  mapUnadoptedVenuesResponse,
} from 'src/utils/crowsNestUnadoptedVenues'
import { openVenueDashboard } from 'src/utils/spartacusVenueActions'
import { crowsNestErrorMessage } from 'src/utils/crowsNestErrors'
import styled from 'styled-components/macro'
import CrowsNestTipLabel from './CrowsNestTipLabel'

const { Option } = Select

/**
 * Venues that order but never adopted recipes / stock / par — Free and paid.
 */
const CrowsNestUnadoptedVenues = () => {
  const [planFilter, setPlanFilter] = useState('')
  const [featureFilter, setFeatureFilter] = useState('')
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [summary, setSummary] = useState(null)

  const loadList = useCallback(async ({ nextPage, plan, feature } = {}) => {
    setLoading(true)
    setError(null)
    try {
      const query = {
        page: nextPage,
        pageSize: CROWS_NEST_UNADOPTED_PAGE_SIZE,
      }
      if (plan) query.plan = plan
      if (feature) query.feature = feature

      const response = await fetchCrowsNestUnadoptedVenues(query)
      setSummary(mapUnadoptedVenuesResponse(response))
    } catch (err) {
      setError(
        crowsNestErrorMessage(
          err,
          "Couldn't load unadopted venues. Try again in a moment.",
        ),
      )
      setSummary(null)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadList({ nextPage: page, plan: planFilter, feature: featureFilter })
  }, [featureFilter, loadList, page, planFilter])

  const columns = useMemo(
    () => [
      {
        title: 'Venue',
        key: 'venue',
        render: (_value, row) => (
          <VenueButton
            type='button'
            onClick={() => openVenueDashboard(row.id)}
            disabled={!row.id}
            data-testid={`unadopted-venue-${row.id}`}
          >
            {row.name}
          </VenueButton>
        ),
      },
      {
        title: 'Plan',
        key: 'plan',
        width: 140,
        render: (_value, row) => (
          <PlanCell>
            <Tag color={row.paid ? 'blue' : undefined}>{row.plan}</Tag>
            <PaidLabel data-testid={`unadopted-paid-${row.id}`}>
              {row.paidLabel}
            </PaidLabel>
          </PlanCell>
        ),
      },
      {
        title: 'Gaps',
        key: 'gaps',
        render: (_value, row) => (
          <span data-testid={`unadopted-gaps-${row.id}`}>{row.gapLabel}</span>
        ),
      },
      {
        title: 'Suppliers',
        dataIndex: 'activeSupplierCount',
        key: 'suppliers',
        width: 110,
      },
    ],
    [],
  )

  const totals = summary?.totals

  return (
    <StatsCard
      data-testid='crows-nest-unadopted-venues'
      title={
        <TitleBlock>
          <TitleRow>
            <span>Unadopted venues</span>
            <Tooltip title={CROWS_NEST_TOOLTIPS.unadoptedSection}>
              <InfoCircleOutlined />
            </Tooltip>
          </TitleRow>
          {summary ? (
            <ScopeLabel data-testid='unadopted-scope-label'>
              {summary.scopeLabel}
            </ScopeLabel>
          ) : null}
        </TitleBlock>
      }
      extra={
        <FilterRow>
          <Select
            value={planFilter}
            onChange={value => {
              setPage(1)
              setPlanFilter(value)
            }}
            style={{ width: 140 }}
            data-testid='unadopted-plan-filter'
          >
            {CROWS_NEST_UNADOPTED_PLAN_FILTERS.map(option => (
              <Option key={option.value || 'all-plans'} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
          <Select
            value={featureFilter}
            onChange={value => {
              setPage(1)
              setFeatureFilter(value)
            }}
            style={{ width: 200 }}
            data-testid='unadopted-feature-filter'
          >
            {CROWS_NEST_UNADOPTED_FEATURE_FILTERS.map(option => (
              <Option key={option.value || 'any-gap'} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>
        </FilterRow>
      }
    >
      {error && !summary ? (
        <Alert type='error' showIcon message={error} />
      ) : null}

      {loading && !summary ? (
        <LoadingWrap>
          <Spin />
        </LoadingWrap>
      ) : null}

      {totals ? (
        <TotalsRow>
          <TotalStat>
            <TotalLabel>
              <CrowsNestTipLabel
                label='Venues'
                tip={CROWS_NEST_TOOLTIPS.unadoptedVenues}
              />
            </TotalLabel>
            <TotalValue data-testid='unadopted-total-venues'>
              {totals.venues}
            </TotalValue>
          </TotalStat>
          <TotalStat>
            <TotalLabel>
              <CrowsNestTipLabel
                label='Paid'
                tip={CROWS_NEST_TOOLTIPS.unadoptedPaid}
              />
            </TotalLabel>
            <TotalValue data-testid='unadopted-total-paid'>
              {totals.paid}
            </TotalValue>
          </TotalStat>
          <TotalStat>
            <TotalLabel>
              <CrowsNestTipLabel
                label='Free'
                tip={CROWS_NEST_TOOLTIPS.unadoptedFree}
              />
            </TotalLabel>
            <TotalValue data-testid='unadopted-total-free'>
              {totals.free}
            </TotalValue>
          </TotalStat>
        </TotalsRow>
      ) : null}

      {summary ? (
        <Table
          rowKey='key'
          columns={columns}
          dataSource={summary.venues}
          loading={loading}
          pagination={{
            current: summary.pagination.page || page,
            pageSize:
              summary.pagination.pageSize || CROWS_NEST_UNADOPTED_PAGE_SIZE,
            total: summary.pagination.total || 0,
            showSizeChanger: false,
            onChange: nextPage => setPage(nextPage),
          }}
          locale={{ emptyText: 'No unadopted venues in this snapshot.' }}
          scroll={{ x: true }}
        />
      ) : null}
    </StatsCard>
  )
}

const StatsCard = styled(Card)`
  .ant-card-head-title {
    font-weight: 600;
  }

  .ant-table-thead > tr > th {
    white-space: nowrap;
  }
`

const TitleBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TitleRow = styled.span`
  align-items: center;
  display: inline-flex;
  gap: 8px;

  .anticon {
    color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
    font-size: 14px;
  }
`

const ScopeLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
  font-weight: 500;
`

const FilterRow = styled.div`
  align-items: center;
  display: inline-flex;
  gap: 8px;
`

const LoadingWrap = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  min-height: 120px;
`

const TotalsRow = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.md || '16px'};
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  margin-bottom: ${({ theme }) => theme.spacing.md || '16px'};
`

const TotalStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const TotalLabel = styled.div`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 13px;
  font-weight: 500;
`

const TotalValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  line-height: 1.1;
`

const PlanCell = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const PaidLabel = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#666'};
  font-size: 12px;
`

const VenueButton = styled.button`
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a6b3a'};
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;

  &:disabled {
    color: inherit;
    cursor: default;
  }
`

export default CrowsNestUnadoptedVenues
